import { DocumentNode, gql } from '@apollo/client';
import React from 'react';
import { ButtonBar } from '../../ButtonBar';
import { CreateOrUpdateInvoiceHandler } from '../types';
import {
	useInvoiceEditButtonBarVoidButton,
	useInvoiceEditButtonBarVoidButtonInvoiceFragment,
} from './useInvoiceEditButtonBarVoidButton';
import {
	useInvoiceEditButtonBarSaveButton,
	useInvoiceEditButtonBarSaveButtonInvoiceFragment,
} from './useInvoiceEditButtonBarSaveButton';
import {
	useInvoiceEditButtonBarSendButton,
	useInvoiceEditButtonBarSendButtonInvoiceFragment,
	useInvoiceEditButtonBarSendButtonProps,
} from './useInvoiceEditButtonBarSendButton';
import { InvoiceEditButtonBarInvoiceFragment } from '#types';
import { createBundleObject } from '../../../lib/helpers';

const invoiceEditButtonBarInvoiceFragment = gql`
	fragment invoiceEditButtonBarInvoice on Invoice {
		id
		status
		...useInvoiceEditButtonBarSaveButtonInvoice
		...useInvoiceEditButtonBarVoidButtonInvoice
		...useInvoiceEditButtonBarSendButtonInvoice
	}
	${ useInvoiceEditButtonBarSaveButtonInvoiceFragment }
	${ useInvoiceEditButtonBarVoidButtonInvoiceFragment }
	${ useInvoiceEditButtonBarSendButtonInvoiceFragment }
`;

export type InvoiceEditButtonBarProps = {
	invoice?: Omit<InvoiceEditButtonBarInvoiceFragment, 'id'> & {
		id?: string;
	};
	onCreateOrUpdateHandler: CreateOrUpdateInvoiceHandler;
	disabled?: boolean;
	bundle?: NonNullable<ReturnType<typeof createBundleObject>>;
	setContactOptional: React.Dispatch<React.SetStateAction<boolean>>;
} & Pick<
	useInvoiceEditButtonBarSendButtonProps,
	| 'openCustomizeEmailModal'
	| 'setOpenCustomizeEmailModal'
	| 'setSkipInstallmentCalculation'
	| 'validate'
	| 'initialStateReady'
>;

type InvoiceEditButtonBarPropsWithFragment =
	React.FC<InvoiceEditButtonBarProps> & {
		fragments: {
			invoiceEditButtonBarInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceEditButtonBar: InvoiceEditButtonBarPropsWithFragment = ( {
	invoice,
	onCreateOrUpdateHandler,
	disabled,
	bundle,
	setContactOptional,
	openCustomizeEmailModal,
	setOpenCustomizeEmailModal,
	setSkipInstallmentCalculation,
	validate,
	initialStateReady,
} ) => {
	const { button: saveButton } = useInvoiceEditButtonBarSaveButton( {
		invoice,
		onCreateOrUpdateHandler,
		disabled,
		setContactOptional,
	} );
	const { button: sendButton, jsx: sendButtonJSX } =
		useInvoiceEditButtonBarSendButton( {
			invoice,
			onCreateOrUpdateHandler,
			disabled,
			bundle,
			setContactOptional,
			openCustomizeEmailModal,
			setOpenCustomizeEmailModal,
			setSkipInstallmentCalculation,
			validate,
			initialStateReady,
		} );
	const { button: voidButton } = useInvoiceEditButtonBarVoidButton( {
		disabled,
	} );
	return (
		<>
			<ButtonBar
				buttons={ {
					...voidButton,
					...saveButton,
					...sendButton,
				} }
			/>
			{ sendButtonJSX }
		</>
	);
};

InvoiceEditButtonBar.fragments = {
	invoiceEditButtonBarInvoiceFragment,
};
